import { useState } from 'react'
import { Box, CircularProgress, Collapse } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { FONT_MONO, FONT_SANS, TOKENS } from './OptimiserDesign'
import { RunResult, useOptimiserRuns } from './Optimiser.data'

// ── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined) =>
  n == null
    ? '—'
    : new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
      }).format(n)

const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${(n * 100).toFixed(1)}%`

const formatRuntime = (s: number | null | undefined) => {
  if (s == null) return '—'
  return s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`
}

// Parse run_id "opt-YYYYMMDD-HHMMSS" → "22 Jun 2026, 14:30"
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const parseRunDate = (runId: string) => {
  const m = runId.match(/opt-(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/)
  if (!m) return runId
  return `${parseInt(m[3])} ${MONTHS[parseInt(m[2]) - 1]} ${m[1]}, ${m[4]}:${m[5]}`
}

const PHASE_LABELS: Record<number, string> = {
  1: 'Basic',
  2: '+ Service',
  3: '+ Capacity',
  4: '+ Commits',
  5: '+ Materials',
  6: '+ MOQs',
}

const statusColor = (tc: string) => {
  if (tc === 'optimal' || tc === 'feasible') return '#22c55e'
  if (tc === 'infeasible') return '#ef4444'
  if (tc === 'maxTimeLimit' || tc === 'timeout') return '#f59e0b'
  return '#94a3b8'
}

const isOk = (tc: string) => tc === 'optimal' || tc === 'feasible'

// ── Sub-components ─────────────────────────────────────────────────────────

const ColHeader = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <Box
    component="th"
    sx={{
      px: '10px',
      py: '7px',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: TOKENS.ink300,
      fontFamily: FONT_SANS,
      textAlign: align,
      borderBottom: `2px solid ${TOKENS.line200}`,
      whiteSpace: 'nowrap',
      background: TOKENS.panelHead,
    }}
  >
    {children}
  </Box>
)

const KpiCard = ({
  label,
  value,
  accent,
}: {
  label: string
  value: string
  accent?: 'green' | 'red' | 'blue'
}) => {
  const colors = {
    green: { bg: '#f0fdf4', value: '#065f46', label: '#059669' },
    red: { bg: '#fef2f2', value: '#991b1b', label: '#dc2626' },
    blue: { bg: TOKENS.blue50, value: TOKENS.brandTeal, label: TOKENS.blue600 },
  }
  const c = colors[accent ?? 'blue']
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 90,
        background: c.bg,
        borderRadius: '6px',
        p: '8px 10px',
        textAlign: 'center',
      }}
    >
      <Box sx={{ fontSize: 15, fontWeight: 700, fontFamily: FONT_MONO, color: c.value, letterSpacing: '-0.02em' }}>
        {value}
      </Box>
      <Box sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: c.label, fontFamily: FONT_SANS, mt: '2px' }}>
        {label}
      </Box>
    </Box>
  )
}

const OutputButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: '10px',
      py: '4px',
      borderRadius: '4px',
      fontSize: 11,
      fontWeight: 600,
      fontFamily: FONT_SANS,
      cursor: 'pointer',
      border: `1px solid ${TOKENS.line300}`,
      background: TOKENS.surface0,
      color: TOKENS.ink700,
      userSelect: 'none',
      '&:hover': { borderColor: TOKENS.brandTeal, color: TOKENS.brandTeal },
    }}
  >
    {label}
  </Box>
)

// ── Run row ────────────────────────────────────────────────────────────────

const RunRow = ({
  run,
  expanded,
  onToggle,
  onViewOutput,
}: {
  run: RunResult
  expanded: boolean
  onToggle: () => void
  onViewOutput: (runId: string, tableName: string) => void
}) => {
  const ok = isOk(run.termination_condition)
  const cb = run.cost_breakdown

  return (
    <>
      <Box
        component="tr"
        onClick={onToggle}
        sx={{
          cursor: 'pointer',
          background: expanded ? TOKENS.blue50 : 'transparent',
          '&:hover': { background: TOKENS.blue50 },
          '& td': { borderBottom: expanded ? 'none' : `1px solid ${TOKENS.line100}` },
        }}
      >
        {/* Status dot */}
        <Box component="td" sx={{ px: '10px', py: '9px', width: 28 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: statusColor(run.termination_condition),
            }}
          />
        </Box>

        {/* Date / run id */}
        <Box component="td" sx={{ px: '10px', py: '9px', minWidth: 160 }}>
          <Box sx={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.ink700, fontFamily: FONT_SANS }}>
            {parseRunDate(run.run_id)}
          </Box>
          <Box sx={{ fontSize: 10, color: TOKENS.ink300, fontFamily: FONT_MONO, mt: '1px' }}>
            {run.run_id}
          </Box>
        </Box>

        {/* Phase */}
        <Box component="td" sx={{ px: '10px', py: '9px', whiteSpace: 'nowrap' }}>
          <Box
            sx={{
              display: 'inline-block',
              px: '7px',
              py: '2px',
              borderRadius: '3px',
              fontSize: 11,
              fontWeight: 700,
              fontFamily: FONT_SANS,
              background: TOKENS.blue50,
              color: TOKENS.blue600,
            }}
          >
            {run.phase} — {PHASE_LABELS[run.phase] ?? ''}
          </Box>
        </Box>

        {/* Service mode */}
        <Box component="td" sx={{ px: '10px', py: '9px', fontSize: 12, color: TOKENS.ink400, fontFamily: FONT_SANS, whiteSpace: 'nowrap' }}>
          {run.service_mode === 'lost_sales' ? 'Lost Sales' : 'Backorder'}
        </Box>

        {/* Status */}
        <Box component="td" sx={{ px: '10px', py: '9px', whiteSpace: 'nowrap' }}>
          <Box
            sx={{
              display: 'inline-block',
              px: '7px',
              py: '2px',
              borderRadius: '3px',
              fontSize: 11,
              fontWeight: 700,
              fontFamily: FONT_SANS,
              background: ok ? '#f0fdf4' : '#fef2f2',
              color: ok ? '#059669' : '#dc2626',
            }}
          >
            {run.termination_condition}
          </Box>
        </Box>

        {/* Cost */}
        <Box component="td" sx={{ px: '10px', py: '9px', fontSize: 12, fontFamily: FONT_MONO, color: TOKENS.ink700, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {fmt(run.objective)}
        </Box>

        {/* Runtime */}
        <Box component="td" sx={{ px: '10px', py: '9px', fontSize: 12, fontFamily: FONT_MONO, color: TOKENS.ink400, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {formatRuntime(run.runtime_s)}
        </Box>

        {/* Expand chevron */}
        <Box component="td" sx={{ px: '10px', py: '9px', color: TOKENS.ink300, textAlign: 'right' }}>
          {expanded
            ? <ExpandLessIcon sx={{ fontSize: 15 }} />
            : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
        </Box>
      </Box>

      {/* Expanded detail */}
      {expanded && (
        <Box component="tr">
          <Box
            component="td"
            colSpan={8}
            sx={{ px: '16px', pb: '16px', borderBottom: `1px solid ${TOKENS.line200}` }}
          >
            {ok ? (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <KpiCard label="Service Level" value={pct(run.kpis?.service_level)} accent="green" />
                  <KpiCard label="Total Cost" value={fmt(run.objective)} accent="blue" />
                  <KpiCard label="Landed" value={fmt(cb?.landed)} accent="blue" />
                  <KpiCard label="Holding" value={fmt(cb?.holding)} accent="blue" />
                  <KpiCard label="Svc Penalty" value={fmt(cb?.service)} accent={cb?.service > 0 ? 'red' : 'blue'} />
                </Box>
                {run.outputs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {run.outputs.map((t) => (
                      <OutputButton
                        key={t}
                        label={t.replace(/_/g, ' ')}
                        onClick={() => onViewOutput(run.run_id, t)}
                      />
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ fontSize: 12, color: '#991b1b', fontFamily: FONT_SANS }}>
                Solve did not complete: {run.termination_condition}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  )
}

// ── Root component ─────────────────────────────────────────────────────────

interface RunHistoryPanelProps {
  onViewOutput: (runId: string, tableName: string) => void
  scenarioId?: string
}

export const RunHistoryPanel = ({ onViewOutput, scenarioId }: RunHistoryPanelProps) => {
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null)
  const { data, isLoading, isError } = useOptimiserRuns(scenarioId)

  const toggle = (runId: string) =>
    setExpandedRunId((id) => (id === runId ? null : runId))

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: FONT_SANS,
      }}
    >
      {/* Header */}
      <Box sx={{ px: '36px', pt: '32px', pb: '16px', flexShrink: 0 }}>
        <Box sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.ink900, mb: 0.5 }}>
          Run History
        </Box>
        <Box sx={{ fontSize: 12, color: TOKENS.ink400 }}>
          {data ? `${data.total} run${data.total !== 1 ? 's' : ''}` : 'Loading…'}
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: '36px' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} thickness={3} sx={{ color: TOKENS.brandTeal }} />
          </Box>
        )}

        {isError && (
          <Box sx={{ fontSize: 12, color: '#991b1b', py: 4, textAlign: 'center' }}>
            Failed to load run history.
          </Box>
        )}

        {data && data.runs.length === 0 && (
          <Box sx={{ fontSize: 13, color: TOKENS.ink300, py: 8, textAlign: 'center' }}>
            No runs yet. Use Run Solver to create your first run.
          </Box>
        )}

        {data && data.runs.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: 640,
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  <ColHeader> </ColHeader>
                  <ColHeader>Run</ColHeader>
                  <ColHeader>Phase</ColHeader>
                  <ColHeader>Mode</ColHeader>
                  <ColHeader>Status</ColHeader>
                  <ColHeader align="right">Cost</ColHeader>
                  <ColHeader align="right">Time</ColHeader>
                  <ColHeader> </ColHeader>
                </Box>
              </Box>
              <Box component="tbody">
                {data.runs.map((run) => (
                  <RunRow
                    key={run.run_id}
                    run={run}
                    expanded={expandedRunId === run.run_id}
                    onToggle={() => toggle(run.run_id)}
                    onViewOutput={onViewOutput}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
